import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DateTime } from 'luxon';

import {
  useCrmCredential,
  useCrmBranches,
  useSaveCrmCredential,
  useLinkBranchTeam,
  useUnlinkBranchTeam
} from '@api';
import type { CrmBranch } from '@api';
import StreetSyncDialog from './StreetSyncDialog';

/**
 * CRM credential and branch provisioning for one company.
 *
 * Rendered from the CRM's declared capabilities rather than from its name — a
 * per-user CRM reports `manageable: false` and this shows why, instead of
 * offering a form that could never do anything. Adding another company-scoped
 * CRM needs no change here.
 *
 * The token is write-only: the API never returns a stored value, so there is
 * deliberately nothing to display and no "reveal" affordance. What an admin can
 * see is whether one is set and when it last verified.
 */

interface CrmCredentialPanelProps {
  companyId?: string;
  /** Called after a Street sync writes teams or users, so the page can refetch. */
  onSynced?: () => void;
}

const formatWhen = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('d LLL yyyy, HH:mm') : null;

export const CrmCredentialPanel: React.FC<CrmCredentialPanelProps> = ({ companyId, onSynced }) => {
  const [token, setToken] = useState('');
  // `intro` is set when the dialog opened itself after an account's first token.
  const [syncDialog, setSyncDialog] = useState<{ intro?: string } | null>(null);

  const { data: status, isLoading } = useCrmCredential(companyId);
  const saveToken = useSaveCrmCredential(companyId);
  const { data: branchData } = useCrmBranches(companyId, Boolean(status?.manageable));
  const linkTeam = useLinkBranchTeam(companyId);
  const unlinkTeam = useUnlinkBranchTeam(companyId);

  const onPickTeam = (branch: CrmBranch, teamId: string) => {
    if (teamId === branch.team?.id) return;
    if (teamId) {
      linkTeam.mutate({ externalId: branch.externalId, teamId });
      return;
    }
    if (branch.team) unlinkTeam.mutate({ teamId: branch.team.id });
  };

  if (!companyId) return null;

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Reapit and GHL hold per-user OAuth tokens — there is no company-level
  // credential for a superadmin to manage, and saying so beats a dead form.
  if (!status?.manageable) {
    return (
      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          CRM connection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {status?.reason ?? 'No CRM configured for this company.'}
        </Typography>
      </Box>
    );
  }

  const lastValidated = formatWhen(status.lastValidatedAt);
  const saveResult = saveToken.data;
  const branches = branchData?.branches ?? [];
  const teams = branchData?.teams ?? [];
  const stale = branchData?.stale ?? [];
  // A team already mapped to another branch can't take this one, so it is shown
  // disabled with the reason rather than silently missing.
  const takenBy = new Map(
    branches.flatMap((b) => (b.team ? [[b.team.id, b.name] as const] : []))
  );

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="subtitle2">CRM connection</Typography>
        <Chip size="small" label={status.crm} />
        {status.present ? (
          <Chip size="small" color="success" label="Token set" />
        ) : (
          <Chip size="small" color="warning" label="No token" />
        )}
      </Stack>

      {/* A misconfigured environment surfaces here rather than as a confusing
          failure the first time someone presses Save. */}
      {!status.encryptionReady && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Credential encryption is not configured on this environment
          (<code>CRM_CREDENTIALS_KEY</code>). Tokens cannot be saved until it is.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {status.crm} authenticates with a single company-wide token, generated by the agency&apos;s
        own admin under <em>Settings → Account Administration → Applications</em>. It is stored
        encrypted and never shown again — if it is lost, regenerate it in {status.crm}.
      </Typography>

      {lastValidated && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Last verified {lastValidated}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          size="small"
          type="password"
          label={status.present ? 'Replace token' : 'Paste token'}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          disabled={!status.encryptionReady || saveToken.isPending}
          autoComplete="off"
        />
        <Button
          variant="contained"
          disabled={!token.trim() || !status.encryptionReady || saveToken.isPending}
          onClick={() =>
            saveToken.mutate(token.trim(), {
              onSuccess: (result) => {
                setToken('');
                // First token on a Street account: offer the sync straight away.
                // It opens on a preview, so nothing is written without a confirm.
                if (result?.valid && result.firstSave && result.syncAvailable) {
                  setSyncDialog({
                    intro: `Token saved. Here is what this ${status.crm} account holds — confirm to bring its branches in as teams and its staff in as users.`
                  });
                }
              }
            })
          }
          sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
        >
          {saveToken.isPending ? 'Verifying…' : 'Verify & save'}
        </Button>
      </Stack>

      {/* The server validates before storing, so a rejected token leaves the
          previous state untouched — worth saying, so an admin isn't left
          wondering whether they have just broken a working connection. */}
      {saveResult && (
        <Alert severity={saveResult.valid ? 'success' : 'error'} sx={{ mt: 1.5 }}>
          {saveResult.detail}
          {!saveResult.valid && ' Nothing was saved — the previous connection is unchanged.'}
        </Alert>
      )}
      {saveToken.isError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          Could not reach the server to verify the token. Nothing was saved.
        </Alert>
      )}


      <Divider sx={{ my: 2 }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2">Branches</Typography>
        {/* One action: it previews first (counts, renames, stale mappings — what
            "Check against" used to report) and writes only on confirm. */}
        {branchData?.supported && (
          <Button size="small" variant="outlined" disabled={!status.present} onClick={() => setSyncDialog({})}>
            Sync from {status.crm}
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Read live from {status.crm} — nothing is stored but the mapping. A negotiator&apos;s branch
        is their team, so pick the team that represents each office and assign people to it as
        usual. Names and ids here are {status.crm}&apos;s; your team names stay yours.
      </Typography>

      {!status.present && (
        <Typography variant="body2" color="text.secondary">
          Save a token first, then branches can be read.
        </Typography>
      )}

      {status.present && branches.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {status.crm} returned no branches for this account.
        </Typography>
      )}

      <Stack spacing={1.25}>
        {branches.map((branch) => (
          <Stack
            key={branch.externalId}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{branch.name}</Typography>
                {branch.renamedInCrm && (
                  <Tooltip title={`Renamed in ${status.crm}. Your team keeps its own name — rename it yourself if you want them to match.`}>
                    <Chip size="small" variant="outlined" color="warning" label="Renamed" />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {branch.externalId}
                {branch.team
                  ? ` · ${branch.team.memberCount} negotiator${branch.team.memberCount === 1 ? '' : 's'}`
                  : ''}
              </Typography>
            </Stack>

            <Select
              size="small"
              displayEmpty
              value={branch.team?.id ?? ''}
              disabled={linkTeam.isPending || unlinkTeam.isPending}
              onChange={(event) => onPickTeam(branch, String(event.target.value))}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">
                <em>Not mapped</em>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem
                  key={team.id}
                  value={team.id}
                  disabled={takenBy.has(team.id) && team.id !== branch.team?.id}
                >
                  {team.teamName}
                  {takenBy.has(team.id) && team.id !== branch.team?.id
                    ? ` (${takenBy.get(team.id)})`
                    : ''}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        ))}
      </Stack>

      {/* A branch that has left the CRM is only reported. The team keeps its
          members and its name — closing an office is a person's decision. */}
      {stale.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {stale.length} team{stale.length === 1 ? ' is' : 's are'} mapped to a branch {status.crm} no
          longer lists: {stale.map((t) => t.name).join(', ')}. Nothing has been changed — unmap or
          remap {stale.length === 1 ? 'it' : 'them'} when you know why.
        </Alert>
      )}

      {linkTeam.isError && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {(linkTeam.error as Error)?.message}
        </Alert>
      )}
      {unlinkTeam.isError && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {(unlinkTeam.error as Error)?.message}
        </Alert>
      )}

      <StreetSyncDialog
        open={Boolean(syncDialog)}
        companyId={companyId}
        intro={syncDialog?.intro}
        onClose={() => setSyncDialog(null)}
        onSynced={onSynced}
      />
    </Box>
  );
};

export default CrmCredentialPanel;
